/* Canvello v8 — header behaviour (Task 2.5). sections/cv-header.liquid.
 *
 * <cv-header> wires, per instance (the theme editor re-renders it whole):
 *
 *  Mega menu   Mouse: hovering a nav item opens its panel, another item swaps
 *              it, the logo, the tools or leaving the header close it (v8).
 *              Touch: the first tap on an item opens it, the second follows
 *              the link. Keyboard: the hidden opener after each item (shown on
 *              focus) toggles it, Tab walks into it, Escape closes it and
 *              returns to the opener, tabbing out closes it. The feature
 *              plates' images load when the page is idle (wide screens) or
 *              at the first pointer or focus in the nav; their unveil
 *              replays each time a panel opens, as in v8, where the panel
 *              re-mounts, once the image is there.
 *  Drawer      Native modal <dialog>: the page behind is inert; Tab wraps
 *              inside; Escape, the close button and the scrim close it; the
 *              page does not scroll behind it; it slides in from the inline
 *              end (cvSlide mirrors in RTL) and out again; focus returns to
 *              Browse. It closes itself if the window grows past 1100px.
 *  Language    The utility bar's disclosure: click, Escape, click outside.
 *  Bag count   Server-rendered; then kept current by a `cart:updated` event
 *              on document (detail: { item_count } or { cart }; the bag
 *              drawer and quick add send it from Tasks 4-5), Dawn's pubsub
 *              cart-update (product form, cart page) and a back/forward
 *              restore, the last two by asking /cart.js. A rise pops the
 *              badge (cvPulse) and is announced politely, unless the event
 *              says quiet (the bag announces its own changes, Task 5).
 */
(() => {
  if (customElements.get('cv-header')) return;

  const root = document.documentElement;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const visible = (el) => (el.checkVisibility ? el.checkVisibility() : el.offsetParent !== null);
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  // Counts that land in each plural category (Arabic needs all six); the
  // live region carries the bag-count string rendered at each of them.
  const SAMPLE = { zero: 0, one: 1, two: 2, few: 3, many: 11, other: 100 };

  // Deferred markup (the drawer's body, the language and country lists) sits
  // in <template data-cv-defer> until first shown: built once, then handed to
  // cv-motion like any late content.
  const stamp = (scope) => {
    const tpls = scope.querySelectorAll('template[data-cv-defer]');
    if (!tpls.length) return;
    tpls.forEach((t) => t.replaceWith(t.content));
    if (window.CVMotion) window.CVMotion.scan(scope);
  };

  class CVHeader extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      this.head = this.querySelector('[data-cv-header]');
      this.drawer = this.querySelector('[data-cv-drawer]');
      this.opener = this.querySelector('[data-cv-drawer-open]');
      this.openItem = null;
      this.pinned = false;
      this.pointer = 'mouse';
      if (!this.head) return;
      this.initMega();
      this.initDrawer();
      this.initLocalization();
      this.initBag();
      this.initEditor();
    }

    disconnectedCallback() {
      this.ac.abort();
      if (this.unsubscribe) this.unsubscribe();
      if (this.drawer && this.drawer.open) this.drawer.close();
      root.removeAttribute('data-cv-locked');
    }

    on(target, type, fn, options = {}) {
      target.addEventListener(type, fn, { ...options, signal: this.ac.signal });
    }

    /* ---- Mega menu ------------------------------------------------------- */

    initMega() {
      this.on(document, 'pointerdown', (e) => (this.pointer = e.pointerType || 'mouse'), { capture: true, passive: true });

      this.querySelectorAll('[data-cv-nav-item]').forEach((li) => {
        const panel = li.querySelector('[data-cv-mega]');
        this.on(li, 'pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          if (panel) this.openMega(li);
          else this.closeMega();
        });
        if (!panel) return;

        this.on(li.querySelector('.cv-nav__link'), 'click', (e) => {
          // Keyboard clicks carry pointerType '' and always follow the link.
          const type = e.pointerType ?? this.pointer;
          if ((type !== 'touch' && type !== 'pen') || li.hasAttribute('data-open')) return;
          e.preventDefault();
          this.openMega(li);
        });
        this.on(li.querySelector('[data-cv-mega-toggle]'), 'click', () => {
          if (li.hasAttribute('data-open')) this.closeMega(true);
          else this.openMega(li);
        });
        this.on(li, 'focusout', (e) => {
          if (li.hasAttribute('data-open') && e.relatedTarget && !li.contains(e.relatedTarget)) this.closeMega(true);
        });
      });

      // Plate images are lazy, so phones and tablets (no plates) never fetch
      // them. Where plates show (wide screen, mouse) they are fetched once the
      // page is idle, and in any case at the first pointer or focus in the
      // nav, so they are there by the time a panel opens.
      const nav = this.querySelector('.cv-nav');
      const warm = () =>
        this.querySelectorAll('[data-cv-mega] img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      if (nav) {
        this.on(nav, 'pointerenter', warm, { once: true });
        this.on(nav, 'focusin', warm, { once: true });
      }
      if (window.matchMedia('(min-width: 1181px) and (hover: hover)').matches) {
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
        const later = () => idle(warm, { timeout: 4000 });
        if (document.readyState === 'complete') later();
        else this.on(window, 'load', later, { once: true });
      }

      this.querySelectorAll('[data-cv-head-calm]').forEach((el) =>
        this.on(el, 'pointerenter', (e) => e.pointerType === 'mouse' && this.closeMega())
      );
      this.on(this.head, 'pointerleave', (e) => e.pointerType === 'mouse' && this.closeMega());
      this.on(document, 'click', (e) => {
        if (this.openItem && !this.head.contains(e.target)) this.closeMega(true);
      });
      this.on(document, 'keydown', (e) => {
        if (e.key !== 'Escape' || !this.openItem) return;
        const li = this.openItem;
        const hadFocus = li.contains(document.activeElement);
        this.closeMega(true);
        if (hadFocus) li.querySelector('[data-cv-mega-toggle]').focus();
      });
    }

    openMega(li) {
      if (this.openItem === li) return;
      this.closeMega(true);
      const panel = li.querySelector('[data-cv-mega]');
      panel.hidden = false;
      li.setAttribute('data-open', '');
      li.querySelector('[data-cv-mega-toggle]').setAttribute('aria-expanded', 'true');
      this.openItem = li;
      this.replayUnveil(panel);
    }

    // force: close even while the theme editor has a plate selected.
    closeMega(force = false) {
      const li = this.openItem;
      if (!li || (this.pinned && !force)) return;
      li.querySelector('[data-cv-mega]').hidden = true;
      li.removeAttribute('data-open');
      li.querySelector('[data-cv-mega-toggle]').setAttribute('aria-expanded', 'false');
      this.openItem = null;
      this.pinned = false;
    }

    // cv-motion.js unveiled the plates at load, while they were hidden; play
    // it again on each opening (cv-motion.css: data-cv-u wait → in → done).
    // The wipe waits for its image (up to 2.5s), so it uncovers the photo,
    // not an empty well.
    replayUnveil(panel) {
      const motion = window.CVMotion;
      if (!motion || !motion.motion) return;
      panel.querySelectorAll('[data-unveil]').forEach((el) => {
        clearTimeout(el.cvHeaderTimer);
        el.dataset.cvU = 'wait';
        const img = el.querySelector('img');
        let started = false;
        const start = () => {
          if (started || el.dataset.cvU !== 'wait') return;
          started = true;
          clearTimeout(el.cvHeaderTimer);
          requestAnimationFrame(() => requestAnimationFrame(() => (el.dataset.cvU = 'in')));
          el.cvHeaderTimer = setTimeout(() => (el.dataset.cvU = 'done'), 1700);
        };
        if (!img || (img.complete && img.naturalWidth)) {
          start();
          return;
        }
        img.loading = 'eager';
        img.addEventListener('load', start, { once: true });
        img.addEventListener('error', start, { once: true });
        el.cvHeaderTimer = setTimeout(start, 2500);
      });
    }

    /* ---- Drawer ---------------------------------------------------------- */

    initDrawer() {
      const d = this.drawer;
      if (!d || !this.opener) return;

      this.on(this.opener, 'click', () => this.openDrawer());
      this.on(d, 'cancel', (e) => {
        e.preventDefault();
        this.closeDrawer();
      });
      // However it closed (our slide-out, or Chrome closing it on Escape
      // without a cancel event, which it does to repeated presses with no
      // user activation in between), put the page back.
      this.on(d, 'close', () => {
        d.removeAttribute('data-closing');
        root.removeAttribute('data-cv-locked');
        this.opener.setAttribute('aria-expanded', 'false');
        if (visible(this.opener)) this.opener.focus();
      });
      this.on(d, 'click', (e) => {
        if (e.target.closest('[data-cv-drawer-close]')) {
          this.closeDrawer();
          return;
        }
        // A click on the scrim lands on the dialog itself, outside its box.
        if (e.target === d) {
          const r = d.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.closeDrawer();
          return;
        }
        const toggle = e.target.closest('[data-cv-dnav-toggle]');
        if (toggle) {
          const sub = d.querySelector('#' + CSS.escape(toggle.getAttribute('aria-controls')));
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          if (sub) sub.hidden = !open;
        }
      });
      this.on(d, 'keydown', (e) => {
        if (e.key !== 'Tab') return;
        const items = Array.from(d.querySelectorAll(FOCUSABLE)).filter(visible);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
      const wide = window.matchMedia('(min-width: 1101px)');
      this.on(wide, 'change', () => wide.matches && this.closeDrawer(true));
    }

    openDrawer() {
      const d = this.drawer;
      if (d.open) return;
      this.closeMega(true);
      stamp(d);
      d.removeAttribute('data-closing');
      d.showModal();
      root.setAttribute('data-cv-locked', '');
      this.opener.setAttribute('aria-expanded', 'true');
      const close = d.querySelector('[data-cv-drawer-close]');
      if (close) close.focus();
    }

    closeDrawer(instant = false) {
      const d = this.drawer;
      if (!d || !d.open || d.hasAttribute('data-closing')) return;
      let timer = 0;
      const end = (e) => e.target === d && done();
      const done = () => {
        clearTimeout(timer);
        d.removeEventListener('animationend', end);
        if (d.open) d.close(); // the close listener above restores the page
      };
      if (instant || reduced()) {
        done();
        return;
      }
      d.setAttribute('data-closing', '');
      d.addEventListener('animationend', end);
      timer = setTimeout(done, 700);
    }

    /* ---- Language and currency (bar) ------------------------------------- */

    initLocalization() {
      this.querySelectorAll('[data-cv-loc]').forEach((box) => {
        const btn = box.querySelector('[data-cv-loc-btn]');
        const panel = box.querySelector('[data-cv-loc-panel]');
        if (!btn || !panel) return;
        const set = (open) => {
          if (open) stamp(panel);
          btn.setAttribute('aria-expanded', String(open));
          panel.hidden = !open;
        };
        this.on(btn, 'click', () => set(panel.hidden));
        this.on(document, 'click', (e) => !panel.hidden && !box.contains(e.target) && set(false));
        this.on(box, 'focusout', (e) => !panel.hidden && e.relatedTarget && !box.contains(e.relatedTarget) && set(false));
        this.on(document, 'keydown', (e) => {
          if (e.key !== 'Escape' || panel.hidden) return;
          set(false);
          btn.focus();
        });
      });
    }

    /* ---- Bag count ------------------------------------------------------- */

    initBag() {
      const badge = this.querySelector('[data-cv-bag-n]');
      this.count = badge ? parseInt(badge.textContent, 10) || 0 : 0;

      this.on(document, 'cart:updated', (e) => {
        const detail = e.detail || {};
        const n = detail.item_count ?? (detail.cart && detail.cart.item_count);
        if (Number.isFinite(n)) this.setCount(n, !!detail.quiet);
        else this.refreshCount();
      });
      // Dawn's pubsub.js (loaded before this file).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCount());
      }
      this.on(window, 'pageshow', (e) => e.persisted && this.refreshCount());
    }

    refreshCount() {
      const url = (window.routes && window.routes.cart_url) || '/cart';
      fetch(url + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => cart && this.setCount(cart.item_count))
        .catch(() => {});
    }

    setCount(n, quiet = false) {
      const prev = this.count;
      this.count = n;
      // Looked up each time: Dawn's cart scripts may have swapped the pill's inside.
      const badge = this.querySelector('[data-cv-bag-n]');
      const sr = this.querySelector('[data-cv-bag-sr]');
      const live = this.querySelector('[data-cv-bag-live]');
      const words = this.countWords(n, live);
      if (badge) badge.textContent = String(n);
      if (sr && words) sr.textContent = words;
      if (n === prev) return;
      if (badge && n > prev) {
        badge.removeAttribute('data-pulse');
        void badge.offsetWidth;
        badge.setAttribute('data-pulse', '');
      }
      if (live && words && !quiet) live.textContent = `${live.dataset.updated}: ${words}`;
    }

    countWords(n, live) {
      if (!live) return '';
      let cat = 'other';
      try {
        cat = new Intl.PluralRules(root.lang || 'en').select(n);
      } catch (e) {}
      const key = 'p' + cat.charAt(0).toUpperCase() + cat.slice(1);
      const template = live.dataset[key] || live.dataset.pOther || '';
      return template.replace(String(SAMPLE[cat] ?? SAMPLE.other), String(n));
    }

    /* ---- Theme editor ---------------------------------------------------- */
    // Selecting a feature plate's block opens its menu and keeps it open.

    initEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.on(document, 'shopify:block:select', (e) => {
        const li = this.contains(e.target) && e.target.closest('[data-cv-nav-item]');
        if (!li) return;
        this.openMega(li);
        this.pinned = true;
      });
      this.on(document, 'shopify:block:deselect', (e) => this.contains(e.target) && this.closeMega(true));
    }
  }

  customElements.define('cv-header', CVHeader);
})();
